import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Clapperboard } from "lucide-react";
import {
  HarmonySearchMd,
  HarmonyBookClosed,
  HarmonyImage,
  HarmonyShoppingCart,
  HarmonyWardrobe,
} from "@/components/icons/harmony-icons-inline";
import { cn } from "@/lib/utils";
import { useLeftSidebar } from "@/components/ui/sidebar";
import { isModifiedClick } from "@/lib/link-utils";
import { useSidebarNavigation } from "./hooks/use-sidebar-navigation";
import { trackStudioClicked } from "@/lib/studio-analytics";
import { getModuleHref, modelsPaths, useActiveModule } from "@/modules";
import { SidebarTooltip } from "./SidebarTooltip";

const ICON_SIZE = 18;

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  /** Saf navigasyon: verilirse <Link> render edilir (hedef tıklama anından önce biliniyor). */
  to?: string;
  /** `to` varsa Link'teki yan etki (guard'lı çalışır); yoksa (ör. arama) tam handler. */
  onClick?: () => void;
  /**
   * Analytics — `isModifiedClick` guard'ının ÖNÜNDE çalışır. Cmd/orta tıkla
   * yeni sekmede açmak da bir tıklamadır; olayın düşmemesi gerekir.
   */
  onTrack?: () => void;
  shortcut?: string;
  isSidebarOpen: boolean;
}

/** Tek navigasyon satırı — açıkken label + shortcut, rail modunda ikon + tooltip */
const NavItem: React.FC<NavItemProps> = ({
  icon,
  label,
  active,
  to,
  onClick,
  onTrack,
  shortcut,
  isSidebarOpen,
}) => {
  const className = cn(
    "flex items-center rounded-lg transition-colors focus:outline-none",
    isSidebarOpen
      ? "w-full h-9 px-2.5 gap-2.5"
      : "size-10 justify-center mx-auto",
    active
      ? "bg-module-accent-soft"
      : "hover:bg-bg-secondary dark:hover:bg-gray-800"
  );

  const content = (
    <>
      <span
        className={cn(
          "size-5 flex items-center justify-center shrink-0",
          active
            ? "text-module-accent"
            : "text-text-quaternary dark:text-gray-500"
        )}
      >
        {icon}
      </span>
      {isSidebarOpen && (
        <>
          <span
            className={cn(
              "text-sm truncate",
              active
                ? "font-semibold text-module-accent-strong dark:text-module-accent"
                : "font-medium text-text-secondary dark:text-gray-300"
            )}
          >
            {label}
          </span>
          {shortcut && (
            <span className="ml-auto text-[11px] font-medium text-text-quaternary dark:text-gray-600">
              {shortcut}
            </span>
          )}
        </>
      )}
    </>
  );

  return (
    <SidebarTooltip label={label} disabled={isSidebarOpen}>
      {to ? (
        <Link
          to={to}
          className={className}
          onClick={(e) => {
            onTrack?.(); // analytics guard'sız
            if (isModifiedClick(e)) return; // yeni sekmede açılıyor, sidebar kapatılmasın
            onClick?.();
          }}
        >
          {content}
        </Link>
      ) : (
        <button
          onClick={() => {
            onTrack?.();
            onClick?.();
          }}
          className={className}
        >
          {content}
        </button>
      )}
    </SidebarTooltip>
  );
};

export default function Navigation() {
  const { t } = useTranslation();
  const location = useLocation();
  const { open: isSidebarOpen } = useLeftSidebar();
  const activeModule = useActiveModule();
  const {
    onOpenSearch,
    aiModelsPath,
    libraryPath,
    studioProjectsPath,
    shoppingPath,
    wardrobePath,
    onSidebarLinkClick,
  } = useSidebarNavigation();

  // Aktif navigation'ı belirle — modül path'leri registry'den, literal yok
  const isSearchActive = location.pathname === "/search";
  // Modül kökü (landing) — modül içindeyken nav'ın başında modül adıyla durur,
  // alt ekranlardan landing'e dönüşün tek sabit yolu.
  const moduleHome = activeModule ? getModuleHref(activeModule, "") : null;
  const isModuleHomeActive = moduleHome !== null && location.pathname === moduleHome;
  const isAIModelsActive = location.pathname === modelsPaths.root();
  const isLibraryActive = location.pathname === libraryPath;
  const isStudioActive = location.pathname.startsWith(modelsPaths.studio());
  const isShoppingActive = location.pathname === shoppingPath;
  const isWardrobeActive = location.pathname === wardrobePath;

  const searchShortcut = navigator.platform.toLowerCase().includes("mac")
    ? "⌘⇧F"
    : "Ctrl+Shift+F";

  return (
    <nav
      className={cn(
        "flex flex-col gap-0.5",
        isSidebarOpen ? "px-2" : "px-0"
      )}
    >
      {activeModule && moduleHome && (
        <>
          <NavItem
            icon={<activeModule.icon size={ICON_SIZE} className="size-[18px]" />}
            label={t(activeModule.nameKey)}
            active={isModuleHomeActive}
            to={moduleHome}
            onClick={onSidebarLinkClick}
            isSidebarOpen={isSidebarOpen}
          />
          <div
            className={cn(
              "my-1 h-px bg-border-secondary/70 dark:bg-gray-800",
              isSidebarOpen ? "mx-2.5" : "mx-3"
            )}
          />
        </>
      )}
      <NavItem
        icon={<HarmonySearchMd size={ICON_SIZE} />}
        label={t("common.search")}
        active={isSearchActive}
        onClick={onOpenSearch}
        shortcut={searchShortcut}
        isSidebarOpen={isSidebarOpen}
      />
      <NavItem
        icon={<HarmonyBookClosed size={ICON_SIZE} />}
        label={t("common.aiModels")}
        active={isAIModelsActive}
        to={aiModelsPath}
        onClick={onSidebarLinkClick}
        isSidebarOpen={isSidebarOpen}
      />
      <NavItem
        icon={<HarmonyImage size={ICON_SIZE} />}
        label={t("common.library")}
        active={isLibraryActive}
        to={libraryPath}
        onClick={onSidebarLinkClick}
        isSidebarOpen={isSidebarOpen}
      />
      <NavItem
        icon={<Clapperboard size={ICON_SIZE} />}
        label={t("editorProjects.navTitle")}
        active={isStudioActive}
        to={studioProjectsPath}
        onClick={onSidebarLinkClick}
        onTrack={trackStudioClicked}
        isSidebarOpen={isSidebarOpen}
      />
      {import.meta.env.VITE_APP_ENV !== "prod" && (
        <NavItem
          icon={<HarmonyShoppingCart size={ICON_SIZE} />}
          label={t("common.shopping")}
          active={isShoppingActive}
          to={shoppingPath}
          onClick={onSidebarLinkClick}
          isSidebarOpen={isSidebarOpen}
        />
      )}
      <NavItem
        icon={<HarmonyWardrobe size={ICON_SIZE} />}
        label={t("wardrobe.title")}
        active={isWardrobeActive}
        to={wardrobePath}
        onClick={onSidebarLinkClick}
        isSidebarOpen={isSidebarOpen}
      />
    </nav>
  );
}
